<template>
  <v-dialog v-model="dialog" max-width="600px" persistent>
    <v-card>
      <v-card-title class="headline">
        <v-icon left>mdi-file-sign</v-icon>
        {{ $t('okidoki.send_contract_dialog_title') }}
      </v-card-title>

      <v-card-text>
        <v-alert v-if="error" type="error" dismissible @input="error = null">
          {{ error }}
        </v-alert>

        <v-alert v-if="success" type="success" dismissible>
          {{ success }}
        </v-alert>

        <v-form ref="form" v-model="valid">
          <v-select
            v-model="selectedTemplate"
            :items="templates"
            item-text="name"
            item-value="id"
            :label="$t('okidoki.select_template')"
            :rules="[v => !!v || $t('okidoki.template_required')]"
            outlined
            dense
            :loading="loading"
            :disabled="loading || sending"
          >
            <template v-slot:prepend-inner>
              <v-icon small>mdi-file-document</v-icon>
            </template>
          </v-select>

          <v-divider class="my-4"></v-divider>

          <div class="text-subtitle-2 mb-2">{{ $t('okidoki.booking_info') }}:</div>
          <div class="ml-4">
            <div class="text-body-2">
              <strong>ID:</strong> {{ booking.id }}
            </div>
            <div class="text-body-2">
              <strong>{{ $t('okidoki.client') }}:</strong> {{ clientFullName }}
            </div>
            <div class="text-body-2" v-if="booking.client && booking.client.phone">
              <strong>{{ $t('custom.phone') }}:</strong> {{ booking.client.phone }}
            </div>
            <div class="text-body-2" v-if="booking.client && booking.client.email">
              <strong>Email:</strong> {{ booking.client.email }}
            </div>
          </div>

          <v-alert type="info" dense outlined class="mt-4">
            <div class="text-caption">
              {{ $t('okidoki.send_info') }}
            </div>
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="close"
          :disabled="sending"
        >
          {{ $t('okidoki.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          @click="sendContract"
          :loading="sending"
          :disabled="!valid || sending"
        >
          <v-icon left>mdi-send</v-icon>
          {{ $t('okidoki.send') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'OkidokiContractDialog',
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    booking: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      valid: false,
      loading: false,
      sending: false,
      templates: [],
      selectedTemplate: null,
      error: null,
      success: null
    }
  },

  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },

    clientFullName() {
      if (!this.booking.client) return '-'
      
      // Для юрлица используем entity_name, для физлица - ФИО
      if (this.booking.client.entity) {
        return this.booking.client.entity_name || '-'
      }
      
      const { lastname, name, middlename } = this.booking.client
      return [lastname, name, middlename].filter(Boolean).join(' ') || '-'
    }
  },

  watch: {
    dialog(val) {
      if (val) {
        this.loadTemplates()
      } else {
        this.reset()
      }
    }
  },

  methods: {
    loadTemplates() {
      this.loading = true
      this.error = null

      this.axios.get('/api/v1/okidoki/templates', {
        headers: {
          Authorization: this.$store.getters.getAuthToken
        }
      })
      .then(response => {
        this.templates = response.data.templates || []
        if (this.templates.length === 0) {
          this.error = this.$t('okidoki.no_templates')
        }
      })
      .catch(error => {
        console.error('Error loading templates:', error)
        
        const errorMessage = error.response?.data?.error || ''
        
        if (errorMessage.includes('баланс') || errorMessage.includes('balance')) {
          this.error = this.$t('okidoki.error_insufficient_balance')
        } else if (errorMessage.includes('API') || errorMessage.includes('api_key')) {
          this.error = this.$t('okidoki.error_invalid_api_key')
        } else if (errorMessage) {
          this.error = errorMessage
        } else {
          this.error = this.$t('okidoki.error_general')
        }
      })
      .finally(() => {
        this.loading = false
      })
    },

    sendContract() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.sending = true
      this.error = null
      this.success = null
      this.isLoading = true
      const data = {
        booking_id: this.booking.id,
        template_id: this.selectedTemplate
      }

      this.axios.post('/api/v1/okidoki/send_contract', data, {
        headers: {
          Authorization: this.$store.getters.getAuthToken
        }
      })
      .then(response => {
        if (response.data.success) {
          this.success = this.$t('okidoki.send_success')
          // Обновляем список документов бронирования
          this.$emit('contract-sent', response.data)
          this.close()
        } else {
          const errorMessage = response.data.error || ''
          
          if (errorMessage.includes('баланс') || errorMessage.includes('balance')) {
            this.error = this.$t('okidoki.error_insufficient_balance')
          } else if (errorMessage.includes('API') || errorMessage.includes('api_key')) {
            this.error = this.$t('okidoki.error_invalid_api_key')
          } else if (errorMessage) {
            this.error = errorMessage
          } else {
            this.error = this.$t('okidoki.send_error')
          }
        }
      })
      .catch(error => {
        console.error('Error sending contract:', error)
        
        // Детальная обработка ошибок
        const errorMessage = error.response?.data?.error || ''
        
        if (errorMessage.includes('баланс') || errorMessage.includes('balance')) {
          this.error = this.$t('okidoki.error_insufficient_balance')
        } else if (errorMessage.includes('API') || errorMessage.includes('api_key')) {
          this.error = this.$t('okidoki.error_invalid_api_key')
        } else if (errorMessage) {
          this.error = errorMessage
        } else {
          this.error = this.$t('okidoki.error_general')
        }
      })
      .finally(() => {
        this.sending = false
        this.isLoading = false
      })
    },

    close() {
      this.dialog = false
    },

    reset() {
      this.selectedTemplate = null
      this.error = null
      this.success = null
      this.valid = false
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    }
  }
}
</script>

<style scoped>
.text-body-2 {
  margin-bottom: 4px;
}
</style>

